import { DomainError } from '../errors.js';
import { compareLocalDate, type LocalDate } from '../local-date.js';
import { EQUIPMENT_STATUSES, type EquipmentStatus } from './equipment.js';

/**
 * Arıza ve breakdown (plan M7 Task 17; PMS belgesi §15–§16; ADR 0029 K19, K24, K25, K27). Arıza
 * değiştirilebilir bir kayıttır; kapanışı onaydan türetilir (`defect_report`, `breakdown_report`)
 * ve onaylanmış arıza kilitlidir [NR496 4.6]. Saf modül.
 */

export const DEFECT_KINDS = ['defect', 'breakdown'] as const;
export type DefectKind = (typeof DEFECT_KINDS)[number];

export const DEFECT_SEVERITIES = ['minor', 'major', 'critical'] as const;
export type DefectSeverity = (typeof DEFECT_SEVERITIES)[number];

/** İş akışı (PMS belgesi §16); kapanış saklanmaz, onaydan türetilir. */
export const DEFECT_STATUSES = [
  'open',
  'investigating',
  'repair',
  'testing',
  'verification',
] as const;
export type DefectStatus = (typeof DEFECT_STATUSES)[number];

/**
 * Arızanın kaynağı: elle, FAIL ölçüm (K19), bakım (iş emri; ADR 0096 §6), CM içe aktarma (K20),
 * günlük gözlem, HSEQ raporu.
 */
export const DEFECT_SOURCES = [
  'manual',
  'measurement',
  'work_record',
  'cm_import',
  // Günlük Defect/Observation'dan dönüştürme (ADR 0060 §7)
  'daily_report',
  // HSEQ raporundan (NC / DF / NM / ACC) açılan arıza (ADR 0096 §5)
  'hse_report',
  // Pre-Docking bulgusundan açılan arıza (ADR 0129 Ek Faz 3; R16): önce açık arızalar aranır
  'drydock',
] as const;
export type DefectSource = (typeof DEFECT_SOURCES)[number];

/** Kapanış onayının kayıt türü (ADR 0029 "Sonuçlar"): arıza ve breakdown ayrı kurallarla (K24). */
export function defectRecordType(kind: DefectKind): 'defect_report' | 'breakdown_report' {
  return kind === 'breakdown' ? 'breakdown_report' : 'defect_report';
}

export interface DefectInput {
  kind: DefectKind;
  /** Bildirim anı, ISO-8601 UTC. */
  reportedAt: string;
  tzOffsetMin: number;
  /** Bildirim günü: gemi yerel tarihi. */
  localDate: LocalDate;
  description: string;
  severity: DefectSeverity;
  /** Operasyonel etki (PMS belgesi §15). */
  operationalImpact: boolean;
  immediateAction: string | null;
  investigation: string | null;
  rootCause: string | null;
  correctiveAction: string | null;
  /** Onarım iş emri (düzeltici ya da breakdown). */
  repairWorkOrderId: string | null;
  /** Onarım gerekmiyorsa gerekçesi (K24). */
  noRepairReason: string | null;
  testResult: string | null;
  /** Onarım sonrası durum [NR496 4.3.2d, 4.5.4d]. */
  conditionAfter: EquipmentStatus | null;
  /** Klas bildirim referansı ya da gerekmiyorsa gerekçesi [UR Z20 3.3.1]. */
  classNotificationRef: string | null;
  classNotificationWaiver: string | null;
  source: DefectSource;
  /** Ölçümden ya da CM içe aktarmasından açılan arızanın ölçümü (K19). */
  measurementId: string | null;
  status: DefectStatus;
}

const blank = (value: string | null | undefined) => !value?.trim();
const TEXT_MAX = 4000;

const localDateOf = (at: string, tzOffsetMin: number): LocalDate =>
  new Date(Date.parse(at) + tzOffsetMin * 60_000).toISOString().slice(0, 10);

/**
 * Arıza kaydının kuralları: türler, ciddiyet, durum ve kaynak kapalı listede (`INVALID_DEFECT`);
 * bildirim günü bildirim anının gemi yerel tarihi (`LOCAL_DATE_MISMATCH`) ve an gelecekte değil
 * (`FUTURE_DATE`); açıklama zorunlu; ölçümden ya da CM'den açılan arıza ölçüme bağlı
 * (`MEASUREMENT_REQUIRED`); onarım sonrası durum kapalı listede (`INVALID_CONDITION`).
 */
export function validateDefect(input: DefectInput, context: { serverNow: string }): void {
  const lists: [unknown, readonly string[], string][] = [
    [input.kind, DEFECT_KINDS, 'kind'],
    [input.severity, DEFECT_SEVERITIES, 'severity'],
    [input.status, DEFECT_STATUSES, 'status'],
    [input.source, DEFECT_SOURCES, 'source'],
  ];
  for (const [value, allowed, field] of lists) {
    if (!allowed.includes(value as string)) {
      throw new DomainError('INVALID_DEFECT', `${field}:${String(value)}`);
    }
  }
  if (typeof input.operationalImpact !== 'boolean') {
    throw new DomainError('INVALID_DEFECT', 'operationalImpact');
  }
  const at = Date.parse(input.reportedAt);
  if (Number.isNaN(at) || !Number.isInteger(input.tzOffsetMin)) {
    throw new DomainError('INVALID_DATE', String(input.reportedAt));
  }
  if (input.localDate !== localDateOf(input.reportedAt, input.tzOffsetMin)) {
    throw new DomainError('LOCAL_DATE_MISMATCH', input.localDate);
  }
  if (at > Date.parse(context.serverNow)) throw new DomainError('FUTURE_DATE', input.reportedAt);
  if (blank(input.description)) throw new DomainError('DESCRIPTION_REQUIRED');
  const texts = [
    input.description,
    input.immediateAction,
    input.investigation,
    input.rootCause,
    input.correctiveAction,
    input.noRepairReason,
    input.testResult,
    input.classNotificationRef,
    input.classNotificationWaiver,
  ];
  if (
    texts.some((value) => value !== null && (typeof value !== 'string' || value.length > TEXT_MAX))
  ) {
    throw new DomainError('INVALID_DEFECT', 'text');
  }
  if ((input.source === 'measurement' || input.source === 'cm_import') && !input.measurementId) {
    throw new DomainError('MEASUREMENT_REQUIRED');
  }
  if (
    input.conditionAfter !== null &&
    !(EQUIPMENT_STATUSES as readonly string[]).includes(input.conditionAfter)
  ) {
    throw new DomainError('INVALID_CONDITION', String(input.conditionAfter));
  }
}

const STEP: Readonly<Record<DefectStatus, number>> = {
  open: 0,
  investigating: 1,
  repair: 2,
  testing: 3,
  verification: 4,
};

/**
 * Durum geçişi (PMS belgesi §16): ileri (adım atlanabilir); test ya da doğrulamada sorun çıkarsa
 * onarıma dönülür. Açığa ve incelemeye geri dönüş yok (`INVALID_TRANSITION`).
 */
export function assertDefectTransition(from: DefectStatus, to: DefectStatus): void {
  const a = STEP[from];
  const b = STEP[to];
  if (a === undefined || b === undefined) {
    throw new DomainError('INVALID_TRANSITION', `${String(from)} → ${String(to)}`);
  }
  if (b >= a) return;
  if (to === 'repair' && (from === 'testing' || from === 'verification')) return;
  throw new DomainError('INVALID_TRANSITION', `${from} → ${to}`);
}

/**
 * Kapanış koşulları (K24): kapanış doğrulama adımından gönderilir (`NOT_IN_VERIFICATION`); kök
 * neden ve düzeltici faaliyet zorunlu [ISM 10.2]; onarım iş emri raporlanmış olmalı
 * (`REPAIR_NOT_REPORTED`) ya da onarım gerekmiyorsa gerekçesi (`REPAIR_REQUIRED`); onarım sonrası
 * durum zorunlu ve çalışmaz olamaz [NR496 4.3.2d, 4.5.4d]; klas ekipmanındaki breakdown klas
 * bildirim referansı ya da gerekçe ister [UR Z20 3.3.1].
 */
export function assertDefectClosable(
  defect: DefectInput,
  context: { repairReported: boolean; classEquipment: boolean },
): void {
  if (defect.status !== 'verification') throw new DomainError('NOT_IN_VERIFICATION', defect.status);
  if (blank(defect.rootCause)) throw new DomainError('ROOT_CAUSE_REQUIRED');
  if (blank(defect.correctiveAction)) throw new DomainError('CORRECTIVE_ACTION_REQUIRED');
  if (defect.repairWorkOrderId !== null) {
    if (!context.repairReported) throw new DomainError('REPAIR_NOT_REPORTED');
  } else if (blank(defect.noRepairReason)) {
    throw new DomainError('REPAIR_REQUIRED');
  }
  if (defect.conditionAfter === null) throw new DomainError('CONDITION_AFTER_REQUIRED');
  if (defect.conditionAfter === 'not_operational') throw new DomainError('NOT_OPERATIONAL');
  if (
    defect.kind === 'breakdown' &&
    context.classEquipment &&
    blank(defect.classNotificationRef) &&
    blank(defect.classNotificationWaiver)
  ) {
    throw new DomainError('CLASS_NOTIFICATION_REQUIRED');
  }
}

const PREFIX: Readonly<Record<DefectKind, string>> = { defect: 'DEF', breakdown: 'BD' };

/**
 * Sıradaki numara (K27): arıza `DEF-YYYY-0001`, breakdown `BD-YYYY-0001`; gemi ve yıl başına,
 * yıl gemi yerel tarihinden. Numarayı gemi sunucusu verir; tekil indeks yarışı ayrıca durdurur.
 */
export function nextDefectNo(kind: DefectKind, year: number, existing: readonly string[]): string {
  const prefix = `${PREFIX[kind]}-${year}-`;
  const pattern = new RegExp(`^${prefix}(\\d+)$`);
  const max = existing.reduce((highest, value) => {
    const match = pattern.exec(value);
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);
  return `${prefix}${String(max + 1).padStart(4, '0')}`;
}

/* ---------- PMS dışı (K25) [UR Z20 3.3.3] ---------- */

export type OutOfPmsReason = 'manual' | 'overdue_coc' | 'open_breakdown';

/**
 * "Out of PMS" (K25): ekipmana bağlı vadesi geçmiş ve kapanmamış CoC, açık breakdown ya da
 * gerekçeli elle işaret [UR Z20 3.3.3]. Durum saklanmaz, türetilir. `findings` klas bulgularıdır
 * (düz nesne; tablo M7d'de); `defects` ekipmanın arızaları, kapanış onaydan türetilmiş.
 */
export function outOfPms(
  equipment: { id: string; outOfPmsManual: boolean; outOfPmsReason: string | null },
  findings: readonly { equipmentId: string; dueOn: LocalDate | null; closed: boolean }[],
  defects: readonly { equipmentId: string; kind: DefectKind; closed: boolean }[],
  today: LocalDate,
): { out: boolean; reasons: OutOfPmsReason[] } {
  const reasons: OutOfPmsReason[] = [];
  if (equipment.outOfPmsManual) reasons.push('manual');
  if (
    findings.some(
      (f) =>
        f.equipmentId === equipment.id &&
        !f.closed &&
        f.dueOn !== null &&
        compareLocalDate(f.dueOn, today) < 0,
    )
  ) {
    reasons.push('overdue_coc');
  }
  if (defects.some((d) => d.equipmentId === equipment.id && d.kind === 'breakdown' && !d.closed)) {
    reasons.push('open_breakdown');
  }
  return { out: reasons.length > 0, reasons };
}
